import { createContext, useContext } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { processStepsApi } from '@/api/processStepsApi'
import { loadProcessRunBatch, seedProcessRunBatch } from '@/lib/processRunBatch'
import { useOptionalTenant } from '@/providers/TenantProvider'

/**
 * Whether the form's batch read has settled (z8nrz7e8kw). The per-field process
 * hooks wait for it, so they read the cache the batch filled instead of each
 * asking for itself. Outside a form (the builder, the Process page) nothing
 * provides it and the default lets them read as they always did.
 */
export const ProcessRunBatchContext = createContext<{ ready: boolean }>({ ready: true })

export function useProcessRunBatchReady(): boolean {
  return useContext(ProcessRunBatchContext).ready
}

/**
 * Read every Process field of one form on a run in (at most) three requests,
 * and fill the per-field caches. Returns whether that has settled — success
 * or not: a part that failed (an older backend) leaves its fields to read for
 * themselves, which is the fallback.
 *
 * Keyed outside the ['process-steps'] tree, so a write's invalidations refresh
 * the one field they touch rather than re-reading the whole form. While it is
 * re-reading (a remount after its data went stale) the fields keep showing
 * what they had and wait, so the revisit is one request, not one per field.
 */
export function useProcessRunBatch({
  fieldIds,
  submissionId,
  canExecute,
  enabled,
}: {
  fieldIds: string[]
  submissionId: string | null
  canExecute: boolean
  enabled: boolean
}): { ready: boolean } {
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  const queryClient = useQueryClient()
  const active = enabled && !!tenantId && !!submissionId && fieldIds.length > 0
  const query = useQuery({
    queryKey: ['process-run-batch', tenantId, submissionId, fieldIds.join(','), canExecute],
    enabled: active,
    queryFn: async () => {
      const batch = await loadProcessRunBatch(
        processStepsApi,
        tenantId as string,
        submissionId as string,
        fieldIds,
        canExecute,
      )
      seedProcessRunBatch(queryClient, tenantId as string, submissionId as string, fieldIds, batch)
      return true
    },
    // Every part's failure is already absorbed into the batch; a retry would
    // only hold the fields back.
    retry: false,
  })
  return { ready: !active || (query.isFetched && !query.isFetching) }
}
